"use client";

import type { Column } from "@tanstack/react-table";
import {
  ArrowLeftToLine,
  ArrowRightToLine,
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  EyeOff,
  Pin,
  PinOff,
  X,
} from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface DataTableColumnHeaderProps<TData, TValue>
  extends React.ComponentProps<typeof DropdownMenuTrigger> {
  column: Column<TData, TValue>;
  label: string;
  labels?: {
    sortAsc?: string;
    sortDesc?: string;
    sortReset?: string;
    hide?: string;
    pinLeft?: string;
    pinRight?: string;
    unpin?: string;
  };
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  label,
  labels,
  className,
  ...props
}: DataTableColumnHeaderProps<TData, TValue>) {
  const {
    sortAsc = "Asc",
    sortDesc = "Desc",
    sortReset = "Reset",
    hide = "Hide",
    pinLeft = "Pin to Left",
    pinRight = "Pin to Right",
    unpin = "Unpin",
  } = labels ?? {};

  if (!column.getCanSort() && !column.getCanHide() && !column.getCanPin()) {
    return <div className={cn(className)}>{label}</div>;
  }

  const canPin = column.getCanPin();
  const isPinned = column.getIsPinned();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "-ml-1.5 flex h-8 items-center gap-1.5 rounded-sm px-2 py-1.5 transition-all duration-150 hover:bg-accent/60 focus:outline-none focus:ring-2 focus:ring-ring/20 data-[state=open]:bg-accent/60 [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-foreground/70",
          className,
        )}
        {...props}
      >
        {label}
        {isPinned && (
          <Pin className="!size-3 text-muted-foreground/60" />
        )}
        {column.getCanSort() &&
          (column.getIsSorted() === "desc" ? (
            <ChevronDown />
          ) : column.getIsSorted() === "asc" ? (
            <ChevronUp />
          ) : (
            <ChevronsUpDown />
          ))}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-36">
        {column.getCanSort() && (
          <>
            <DropdownMenuCheckboxItem
              className="relative pr-8 pl-2 [&>span:first-child]:right-2 [&>span:first-child]:left-auto [&_svg]:text-muted-foreground"
              checked={column.getIsSorted() === "asc"}
              onClick={() => column.toggleSorting(false)}
            >
              <ChevronUp />
              {sortAsc}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              className="relative pr-8 pl-2 [&>span:first-child]:right-2 [&>span:first-child]:left-auto [&_svg]:text-muted-foreground"
              checked={column.getIsSorted() === "desc"}
              onClick={() => column.toggleSorting(true)}
            >
              <ChevronDown />
              {sortDesc}
            </DropdownMenuCheckboxItem>
            {column.getIsSorted() && (
              <DropdownMenuItem
                className="pl-2 [&_svg]:text-muted-foreground"
                onClick={() => column.clearSorting()}
              >
                <X />
                {sortReset}
              </DropdownMenuItem>
            )}
          </>
        )}
        {column.getCanSort() && (canPin || column.getCanHide()) && (
          <DropdownMenuSeparator />
        )}
        {canPin && (
          <>
            {isPinned ? (
              <DropdownMenuItem
                className="pl-2 [&_svg]:text-muted-foreground"
                onClick={() => column.pin(false)}
              >
                <PinOff />
                {unpin}
              </DropdownMenuItem>
            ) : (
              <>
                <DropdownMenuItem
                  className="pl-2 [&_svg]:text-muted-foreground"
                  onClick={() => column.pin("left")}
                >
                  <ArrowLeftToLine />
                  {pinLeft}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="pl-2 [&_svg]:text-muted-foreground"
                  onClick={() => column.pin("right")}
                >
                  <ArrowRightToLine />
                  {pinRight}
                </DropdownMenuItem>
              </>
            )}
          </>
        )}
        {canPin && column.getCanHide() && <DropdownMenuSeparator />}
        {column.getCanHide() && (
          <DropdownMenuCheckboxItem
            className="relative pr-8 pl-2 [&>span:first-child]:right-2 [&>span:first-child]:left-auto [&_svg]:text-muted-foreground"
            checked={!column.getIsVisible()}
            onClick={() => column.toggleVisibility(false)}
          >
            <EyeOff />
            {hide}
          </DropdownMenuCheckboxItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
